import {createSlice, createAsyncThunk} from '@reduxjs/toolkit';
import ProviderManager from '../../../provider/providerManager';
import {STREAMING_REGISTRATION_DEFAULTS} from './utils/constants';
import {getInitialStep, getInitialProcessDefinition} from './utils/functions';

const getConnectClientName = (getState) =>
  getState().streamingRegistrationSlice?.connectClientName ?? STREAMING_REGISTRATION_DEFAULTS.connectClientName;

export const getSubscriberRegistrationStatus = createAsyncThunk(
  'streamingRegistrationSlice/getSubscriberRegistrationStatus',
  async (data, {getState}) => {
    try {
      const result = await ProviderManager.getProviderData('getSubscriberRegistrationStatus', [
        data.tvSubscriber,
        data.shouldCreateJsonParameters,
        getConnectClientName(getState),
      ]);

      return {
        data: result?.Body,
        error: false,
      };
    } catch (error) {
      return {
        data: '',
        error: true,
      };
    }
  },
);

export const getJsonParametersBySubscriber = createAsyncThunk(
  'streamingRegistrationSlice/getJsonParametersBySubscriber',
  async (data, {getState}) => {
    try {
      const result = await ProviderManager.getProviderData('getJsonParametersBySubscriber', [
        data.tvSubscriber,
        getConnectClientName(getState),
      ]);

      return {
        data: result?.Body,
        error: false,
      };
    } catch (error) {
      return {
        data: '',
        error: true,
      };
    }
  },
);

export const getTokenBeforeRedirect = createAsyncThunk(
  'streamingRegistrationSlice/getTokenBeforeRedirect',
  async (data) => {
    try {
      const result = await ProviderManager.getProviderData('getTokenBeforeRedirect', [
        data.connectClientName,
        data.subscriberNumber,
        data.idNumber,
        data.jsonParameters,
        data.hash,
        data.sid,
      ]);

      const accessToken = result?.Body?.access_token;
      return {
        accessToken,
        error: false,
      };
    } catch (error) {
      return {
        accessToken: '',
        error: true,
      };
    }
  },
);

export const getDisneyPlusTokenBeforeRedirect = createAsyncThunk(
  'streamingRegistrationSlice/getDisneyPlusTokenBeforeRedirect',
  async (data) => {
    try {
      const result = await ProviderManager.getProviderData('getDisneyPlusTokenBeforeRedirect', [
        data.connectClientName,
        data.subscriberNumber,
        data.idNumber,
        data.sub,
        data.jsonParameters,
        data.hash,
        data.sid,
      ]);

      const accessToken = result?.Body?.access_token;
      return {
        accessToken,
        error: false,
      };
    } catch (error) {
      return {
        accessToken: '',
        error: true,
      };
    }
  },
);

const createInitialState = () => ({
  connectClientName: STREAMING_REGISTRATION_DEFAULTS.connectClientName,
  worldUrlParameter: STREAMING_REGISTRATION_DEFAULTS.worldUrlParameter,
  currentProcess: getInitialProcessDefinition(),
  currentStep: getInitialStep(),
  selectedTvSubscriber: null,
  jsonParameters: null,
  subscriberExternalId: null,
  registrationStatus: null,
  token: null,
});

const initialState = createInitialState();

const streamingRegistrationSlice = createSlice({
  name: 'streamingRegistrationSlice',
  initialState,
  reducers: {
    reset: () => createInitialState(),

    resetFlowState: (state) => {
      state.currentStep = getInitialStep();
      state.registrationStatus = null;
      state.token = null;
      state.jsonParameters = null;
      state.subscriberExternalId = null;
    },

    setRegistrationParams: (state, action) => {
      state.connectClientName = action.payload.connectClientName;
      state.worldUrlParameter = action.payload.worldUrlParameter;
    },
    setCurrentStep: (state, action) => {
      state.currentStep = action.payload;
    },
    setCurrentProcess: (state, action) => {
      state.currentProcess = action.payload;
    },
    setSelectedTvSubscriber: (state, action) => {
      state.selectedTvSubscriber = action.payload;
    },
    setJsonParameters: (state, action) => {
      state.jsonParameters = action.payload;
    },
    setSubscriberExternalId: (state, action) => {
      state.subscriberExternalId = action.payload;
    },
  },
  extraReducers: (builder) => {
    builder.addCase(getSubscriberRegistrationStatus.fulfilled, (state, action) => {
      const {error} = action.payload || {};
      if (!error) {
        const {status, jsonParameters: jsonFromApi, subscriberExternalId} = action.payload?.data || {};
        state.registrationStatus = status;
        if (jsonFromApi && !state.jsonParameters) {
          state.jsonParameters = jsonFromApi;
        }
        if (subscriberExternalId && !state.subscriberExternalId) {
          state.subscriberExternalId = subscriberExternalId;
        }
      }
    });
    builder.addCase(getJsonParametersBySubscriber.fulfilled, (state, action) => {
      const {error} = action.payload || {};
      if (!error) {
        const {jsonParameters, subscriberExternalId, status} = action.payload?.data || {};
        if (status) {
          state.registrationStatus = status;
        }
        if (jsonParameters) {
          state.jsonParameters = jsonParameters;
        }
        if (subscriberExternalId) {
          state.subscriberExternalId = subscriberExternalId;
        }
      }
    });
    builder.addCase(getTokenBeforeRedirect.fulfilled, (state, action) => {
      const {error, accessToken} = action.payload || {};
      if (!error && accessToken) {
        state.token = accessToken;
      }
    });
    builder.addCase(getDisneyPlusTokenBeforeRedirect.fulfilled, (state, action) => {
      const {error, accessToken} = action.payload || {};
      if (!error && accessToken) {
        state.token = accessToken;
      }
    });
  },
});

export const {
  reset,
  resetFlowState,
  setRegistrationParams,
  setCurrentStep,
  setCurrentProcess,
  setSelectedTvSubscriber,
  setJsonParameters,
  setSubscriberExternalId,
} = streamingRegistrationSlice.actions;

export default streamingRegistrationSlice.reducer;